import React, { useState, lazy, Suspense, useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import './styles/main-site-header-fix.css';
import './styles/modal-scroll-lock.css';
import './styles/promo-bar.css';
import './styles/fix-header-overlap.css';
import { AuthProvider } from './contexts/AuthContext';
import { MediaProvider } from './contexts/MediaContext';
import { SiteSettingsProvider, useSiteSettings } from './contexts/SiteSettingsContext';
import { ToursProvider } from './contexts/ToursContext';
import { I18nProvider } from './i18n/I18nContext';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import ClarityLoader from './components/ClarityLoader';
// The one route imported statically. As a lazy chunk it could not start
// painting until main.js had parsed, executed and only THEN discovered the
// dynamic import and fetched the route chunk — a whole extra round trip,
// ~100-300ms of mobile LCP even with the hero image preloaded at
// fetchpriority=high. Static costs +5,912 bytes gzip on main.js; worth it.
//
// /book is deliberately NOT a second exception, though it carries as much
// traffic. Measured over three builds: making BookPage static adds +106,762
// bytes gzip, because its graph drags libphonenumber-js metadata (48KB gzip)
// out of its own async chunk and into the entry bundle — which every visitor,
// including one who never opens /book, would download and parse before the
// homepage could paint. It would make the metric it targets worse.
import HomePage from './pages/HomePage';
import { installAutoInstrumentation, trackPageView } from './utils/sessionAnalytics';

// Lazy-load below-the-fold / non-critical components
const Footer = lazy(() => import('./components/Footer'));
const FloatingButtons = lazy(() => import('./components/FloatingButtons'));
const WhatsAppWidget = lazy(() => import('./components/WhatsAppWidget'));
// HomepagePopup.js was removed from the repo; its dangling import/render are
// dropped here to keep the build green (completes the prior deletion).
const ProtectedRoute = lazy(() => import('./components/ProtectedRoute'));
const TestModeToggle = lazy(() => import('./components/TestModeToggle'));

// Scroll to top on route change — fixes white screen on back navigation
// useLayoutEffect runs synchronously before paint, ensuring scroll is at top
// before intersection observers check element positions
function ScrollToTop() {
  const { pathname } = useLocation();

  // Disable browser's automatic scroll restoration (interferes with SPA navigation)
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    // One-time wire-up of global click/error listeners for the analytics layer.
    // Skip on admin/login routes so our own internal clicks don't pollute the data.
    if (!/^\/(admin|login)/.test(window.location.pathname)) {
      installAutoInstrumentation();
    }
  }, []);

  // Fire a page_view event into the session analytics on every route change.
  useEffect(() => {
    if (!/^\/(admin|login)/.test(pathname)) trackPageView(pathname);
  }, [pathname]);

  // useLayoutEffect fires before paint — ensures scroll is at (0,0) before
  // intersection observers in child components check element positions
  useLayoutEffect(() => {
    // Clean up any stale body scroll locks from modals/popups on previous page
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    document.body.style.overscrollBehavior = '';

    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// Workaround: a Cloudflare Pages redirect rule rewrites `/booking-success` to
// `/` (308), but query params (`session_id`, `booking_id`) are preserved.
// When the root URL has either of those params present, the user is coming
// back from Stripe checkout — show the BookingSuccess page instead of the
// homepage so the confirmation flow is not lost. (Remove this wrapper once
// the offending redirect rule is deleted from the CF dashboard.)
function HomeOrBookingSuccess() {
  const [searchParams] = useSearchParams();
  const fromStripe = searchParams.get('session_id') || searchParams.get('booking_id');
  return fromStripe ? <BookingSuccess /> : <HomePage />;
}

// Lazy load heavy components
const AdminPage = lazy(() => import('./pages/AdminPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
// BookingModal is the new English-language booking flow used everywhere on the site.
// It replaces the legacy Turkish BookingForm. Loading it globally here means the
// Hero "BOOK YOUR RIDE" CTA, the mobile sticky "Book Online" button, and the
// `open-booking-modal` event all open the SAME modal regardless of which page
// the user is on (previously /about, /faq, etc. had no listener and FloatingButtons
// silently failed; on /tours two modals would stack).
const BookingModal = lazy(() =>
  import('./components/BookingModal').catch(() => {
    return new Promise(resolve => setTimeout(resolve, 1000))
      .then(() => import('./components/BookingModal'));
  })
);
const BookingDashboard = lazy(() => import('./components/BookingDashboard'));

// Every other page stays lazy — /book included, see the note above the HomePage
// import. They are reached after a first paint has already happened somewhere
// else, so fetching a chunk on navigation costs less than carrying it in main.js.
const AboutPage = lazy(() => import(/* webpackChunkName: "pg-about" */ './pages/AboutPage'));
const ToursPage = lazy(() => import(/* webpackChunkName: "pg-tours" */ './pages/ToursPage'));
const HorsesPage = lazy(() => import(/* webpackChunkName: "pg-horses" */ './pages/HorsesPage'));
const ContactPage = lazy(() => import(/* webpackChunkName: "pg-contact" */ './pages/ContactPage'));
const FAQPage = lazy(() => import(/* webpackChunkName: "pg-faq" */ './pages/FAQPage'));
const ProposalPage = lazy(() => import(/* webpackChunkName: "pg-proposals" */ './pages/ProposalPage'));
const GalleryPage = lazy(() => import(/* webpackChunkName: "pg-gallery" */ './pages/GalleryPage'));
const BlogPage = lazy(() => import(/* webpackChunkName: "pg-blog" */ './pages/BlogPage'));
const BlogDetailPage = lazy(() => import(/* webpackChunkName: "pg-blog-detail" */ './pages/BlogDetailPage'));
const EventsPage = lazy(() => import(/* webpackChunkName: "pg-events" */ './pages/EventsPage'));
const EventDetailPage = lazy(() => import(/* webpackChunkName: "pg-event-detail" */ './pages/EventDetailPage'));
const ChristmasEventPage = lazy(() => import(/* webpackChunkName: "pg-christmas" */ './pages/ChristmasEventPage'));
const TourDetailPage = lazy(() => import(/* webpackChunkName: "pg-tour-detail" */ './pages/TourDetailPage'));
const ReviewPlatformsPage = lazy(() => import(/* webpackChunkName: "pg-reviews" */ './pages/ReviewPlatformsPage'));
const NotFoundPage = lazy(() => import(/* webpackChunkName: "pg-notfound" */ './pages/NotFoundPage'));
const ServiceAreaPage = lazy(() => import(/* webpackChunkName: "pg-area" */ './pages/ServiceAreaPage'));
const TourGuide = lazy(() => import('./components/TourGuide/TourGuidePWA'));
const PrivacyPolicy = lazy(() => import(/* webpackChunkName: "pg-privacy" */ './components/PrivacyPolicy'));
const TermsOfService = lazy(() => import(/* webpackChunkName: "pg-terms" */ './components/TermsOfService'));
const BookingSuccess = lazy(() => import('./pages/BookingSuccess'));
const GroupPrivatePage = lazy(() => import(/* webpackChunkName: "pg-group-private" */ './pages/GroupPrivatePage'));
const DriverManifestPage = lazy(() => import('./pages/DriverManifestPage'));
const ManageBookingPage = lazy(() => import('./pages/ManageBookingPage'));
const GiftCertificatePage = lazy(() => import(/* webpackChunkName: "pg-gift" */ './pages/GiftCertificatePage'));
const BookPage = lazy(() => import(/* webpackChunkName: "pg-book" */ './pages/BookPage'));
const ComparePage = lazy(() => import(/* webpackChunkName: "pg-compare" */ './pages/ComparePage'));
const PressPage = lazy(() => import(/* webpackChunkName: "pg-press" */ './pages/PressPage'));

// Loading component for better UX
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Loads favicon from shared SiteSettingsContext (no extra API call)
function FaviconLoader() {
  const { settings } = useSiteSettings();
  useEffect(() => {
    const faviconUrl = settings?.favicon;
    if (!faviconUrl) return;
    try {
      const existingIcons = document.querySelectorAll("link[rel*='icon']");
      existingIcons.forEach(icon => icon.remove());
      [
        { rel: 'icon', type: 'image/x-icon' },
        { rel: 'icon', type: 'image/png', sizes: '32x32' },
        { rel: 'icon', type: 'image/png', sizes: '16x16' },
        { rel: 'apple-touch-icon', sizes: '180x180' },
        { rel: 'shortcut icon', type: 'image/x-icon' },
      ].forEach(({ rel, type, sizes }) => {
        const link = document.createElement('link');
        link.rel = rel;
        if (type) link.type = type;
        if (sizes) link.sizes = sizes;
        link.href = faviconUrl;
        document.head.appendChild(link);
      });
    } catch (error) {
      console.error('Error loading favicon:', error);
    }
  }, [settings?.favicon]);
  return null;
}

function App() {
  const [isBookingFormOpen, setIsBookingFormOpen] = useState(false);
  const [isBookingDashboardOpen, setIsBookingDashboardOpen] = useState(false);
  const [selectedTour, setSelectedTour] = useState(null);
  // Abandoned-booking resume: pre-filled form data fetched from the worker
  // when the URL contains ?resume=<token>. Passed into BookingModal as
  // `initialFormData` to restore the customer's progress.
  const [resumeFormData, setResumeFormData] = useState(null);

  // Listen for the global "open-booking-modal" event so any component on the
  // page (e.g. Hero CTA) can open the modal without prop drilling.
  // Hero dispatches this from its "BOOK YOUR RIDE" button.
  useEffect(() => {
    const handler = () => setIsBookingFormOpen(true);
    window.addEventListener('open-booking-modal', handler);
    return () => window.removeEventListener('open-booking-modal', handler);
  }, []);

  // Abandoned booking recovery: when a customer clicks the resume link from
  // their recovery email/SMS (/?resume=<token>), fetch their saved partial
  // state and open the BookingModal pre-filled. Then strip ?resume from the
  // URL so refreshes don't re-trigger.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('resume');
      if (!token) return;
      // /book handles its own resume now (BookPage restores the real funnel).
      // Opening the legacy modal on top of it would strand the guest on a
      // surface that no longer takes bookings.
      if (window.location.pathname.startsWith('/book')) return;
      (async () => {
        try {
          const res = await fetch(`/api/abandoned-bookings?token=${encodeURIComponent(token)}`);
          if (!res.ok) {
            console.log('[RESUME] Token expired or invalid');
            return;
          }
          const state = await res.json();
          setResumeFormData(state);
          setIsBookingFormOpen(true);
        } catch (e) {
          console.log('[RESUME] Failed to fetch abandoned state:', e.message);
        } finally {
          // Strip ?resume from the URL — keep the page clean if user refreshes.
          params.delete('resume');
          const newSearch = params.toString();
          const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
          window.history.replaceState({}, '', newUrl);
        }
      })();
    } catch {}
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <AuthProvider>
          <MediaProvider>
            <SiteSettingsProvider>
            <ToursProvider>
            <I18nProvider>
            <FaviconLoader />
            <Router>
            <ScrollToTop />
            <ClarityLoader />
            <div className="App overflow-x-hidden">
              <Suspense fallback={null}><TestModeToggle /></Suspense>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              {/* Admin routes without Header/Footer */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/admin/*" element={
                <ProtectedRoute>
                  <AdminPage />
                </ProtectedRoute>
              } />
              
              {/* Regular pages with Header/Footer */}
              <Route path="/*" element={
                <div className="main-site-wrapper overflow-x-hidden">
                  <Header />
                  <main id="main-content" className="content-after-header">
                  <Routes>
                    {/* Root route — if Stripe success URL (`/booking-success`)
                        is redirected to `/` by a Cloudflare Pages rule, the
                        `session_id` / `booking_id` query params survive. Render
                        BookingSuccess in that case so the customer still sees
                        their confirmation screen (workaround until the
                        offending redirect rule is removed from CF dashboard). */}
                    <Route path="/" element={<HomeOrBookingSuccess />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/tours" element={<ToursPage />} />
                    <Route path="/tour-guide" element={<TourGuide />} />
                    <Route path="/horses" element={<HorsesPage />} />
                    <Route path="/proposals" element={<ProposalPage />} />
                    <Route path="/gallery" element={<GalleryPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:id" element={<BlogDetailPage />} />
                    <Route path="/events" element={<EventsPage />} />
                    <Route path="/events/christmas" element={<ChristmasEventPage />} />
                    <Route path="/events/:slug" element={<EventDetailPage />} />
                    <Route path="/reviews" element={<ReviewPlatformsPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/booking-success" element={<BookingSuccess />} />
                    <Route path="/group-private" element={<GroupPrivatePage />} />
                    <Route path="/driver" element={<DriverManifestPage />} />
                    <Route path="/manage-booking" element={<ManageBookingPage />} />
                    <Route path="/gift" element={<GiftCertificatePage />} />
                    <Route path="/compare" element={<ComparePage />} />
                    <Route path="/press" element={<PressPage />} />
                    <Route path="/areas/:area" element={<ServiceAreaPage />} />
                    {/* URL-driven booking flow. MUST stay above the "/:slug"
                        catch-all below, or /book is read as a tour slug and
                        renders TourDetailPage instead. */}
                    <Route path="/book" element={<BookPage />} />
                    <Route path="/book/:category" element={<BookPage />} />
                    <Route path="/book/:category/:tour" element={<BookPage />} />
                    <Route path="/book/:category/:tour/:datetime" element={<BookPage />} />
                    <Route path="/:slug" element={<TourDetailPage />} />
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                  </main>
                  <Suspense fallback={<div style={{minHeight:'200px'}} />}>
                    <Footer />
                  </Suspense>
                  <Suspense fallback={null}>
                    <FloatingButtons
                      onOpenBooking={() => setIsBookingFormOpen(true)}
                      onOpenBookingDashboard={() => setIsBookingDashboardOpen(true)}
                    />
                  </Suspense>
                  <Suspense fallback={null}>
                    <WhatsAppWidget />
                  </Suspense>
                </div>
              } />
            </Routes>
          </Suspense>
          
          {isBookingFormOpen && <Suspense fallback={null}>
            <BookingModal
              selectedTour={selectedTour}
              isOpen={isBookingFormOpen}
              initialFormData={resumeFormData}
              onClose={() => {
                setIsBookingFormOpen(false);
                setSelectedTour(null);
                setResumeFormData(null);
              }}
            />
          </Suspense>}
          
          {isBookingDashboardOpen && <Suspense fallback={null}>
            <BookingDashboard
              isOpen={isBookingDashboardOpen}
              onClose={() => setIsBookingDashboardOpen(false)}
            />
          </Suspense>}
            </div>
          </Router>
            </I18nProvider>
            </ToursProvider>
            </SiteSettingsProvider>
            </MediaProvider>
          </AuthProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
